import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, ChevronUp, ChevronDown, Edit, Trash2, User, Upload, Download } from 'lucide-react';
import * as XLSX from 'xlsx';
import { useApp } from '../context/AppContext';
import { formatDate, getStatusBadgeClass, getStatusLabel, getAge } from '../utils/helpers';
import Modal from '../components/ui/Modal';
import Pagination from '../components/ui/Pagination';
import MemberForm from './MemberForm';
import ImportMembersModal from '../components/ImportMembersModal';

const ITEMS_PER_PAGE = 10;

const Members = () => {
  const navigate = useNavigate();
  const { members, deleteMember } = useApp();

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortField, setSortField] = useState('createdAt');
  const [sortDir, setSortDir] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [deleteInput, setDeleteInput] = useState('');

  function handleExport() {
    const rows = members.map(m => ({
      '이름': m.name || '',
      '전화번호': m.phone || '',
      '이메일': m.email || '',
      '성별': m.gender === 'male' ? '남' : m.gender === 'female' ? '여' : '',
      '생년월일': m.birthDate || '',
      '주소': m.address || '',
      '운동목적': m.purpose || '',
      '상태': m.status || '',
      '등록일': m.createdAt ? m.createdAt.slice(0, 10) : '',
      '메모': m.memo || '',
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, '회원목록');
    const today = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(wb, `회원목록_${today}.xlsx`);
  }

  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const filtered = useMemo(() => {
    let list = [...members];
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(m => m.name?.toLowerCase().includes(q) || m.phone?.includes(q));
    }
    if (statusFilter !== 'all') {
      list = list.filter(m => m.status === statusFilter);
    }
    list.sort((a, b) => {
      let av = a[sortField] || '';
      let bv = b[sortField] || '';
      if (sortField === 'name') return sortDir === 'asc' ? av.localeCompare(bv, 'ko') : bv.localeCompare(av, 'ko');
      return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
    });
    return list;
  }, [members, search, statusFilter, sortField, sortDir]);

  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
  const paginated = filtered.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  const handleEdit = (member) => {
    setEditingMember(member);
    setIsFormOpen(true);
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
    setEditingMember(null);
  };

  const handleDelete = (member) => {
    setDeleteInput('');
    setDeleteConfirm(member);
  };

  const confirmDelete = () => {
    if (deleteConfirm && deleteInput === '삭제') {
      deleteMember(deleteConfirm.id);
      setDeleteConfirm(null);
      setDeleteInput('');
    }
  };

  const cancelDelete = () => {
    setDeleteConfirm(null);
    setDeleteInput('');
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <span className="text-gray-300 dark:text-gray-600 ml-1">↕</span>;
    return sortDir === 'asc'
      ? <ChevronUp className="w-3 h-3 inline ml-1 text-primary-500" />
      : <ChevronDown className="w-3 h-3 inline ml-1 text-primary-500" />;
  };

  const statusOptions = [
    { value: 'all', label: '전체' },
    { value: 'active', label: '활성' },
    { value: 'expired', label: '만료' },
    { value: 'inactive', label: '휴면' },
  ];

  return (
    <div className="space-y-4">
      {/* 상단 액션 */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div className="flex flex-1 gap-2 flex-col sm:flex-row w-full sm:w-auto">
          {/* 검색 */}
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="이름 또는 전화번호 검색"
              value={search}
              onChange={e => { setSearch(e.target.value); setCurrentPage(1); }}
              className="input pl-9"
            />
          </div>
          {/* 상태 필터 */}
          <div className="flex gap-1">
            {statusOptions.map(opt => (
              <button
                key={opt.value}
                onClick={() => { setStatusFilter(opt.value); setCurrentPage(1); }}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  statusFilter === opt.value
                    ? 'bg-primary-600 text-white'
                    : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          <button
            onClick={handleExport}
            className="btn-secondary flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            내보내기
          </button>
          <button
            onClick={() => setIsImportOpen(true)}
            className="btn-secondary flex items-center gap-2"
          >
            <Upload className="w-4 h-4" />
            엑셀 가져오기
          </button>
          <button
            onClick={() => { setEditingMember(null); setIsFormOpen(true); }}
            className="btn-primary flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            회원 등록
          </button>
        </div>
      </div>

      {/* 테이블 */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  <button onClick={() => handleSort('name')} className="flex items-center gap-1 hover:text-gray-700 dark:hover:text-gray-200">
                    이름 <SortIcon field="name" />
                  </button>
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden sm:table-cell">
                  성별/나이
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden md:table-cell">
                  전화번호
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden lg:table-cell">
                  운동목적
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  상태
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden lg:table-cell">
                  <button onClick={() => handleSort('createdAt')} className="flex items-center gap-1 hover:text-gray-700 dark:hover:text-gray-200">
                    등록일 <SortIcon field="createdAt" />
                  </button>
                </th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  액션
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {paginated.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center py-12 text-gray-400 dark:text-gray-500">
                    <User className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>회원이 없습니다.</p>
                  </td>
                </tr>
              ) : (
                paginated.map(member => (
                  <tr
                    key={member.id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors cursor-pointer"
                    onClick={() => navigate(`/members/${member.id}`)}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center flex-shrink-0">
                          {member.photo ? (
                            <img src={member.photo} alt={member.name} className="w-9 h-9 rounded-full object-cover" />
                          ) : (
                            <span className="text-sm font-semibold text-primary-700 dark:text-primary-400">
                              {member.name?.[0]}
                            </span>
                          )}
                        </div>
                        <div>
                          <div className="font-medium text-sm text-gray-900 dark:text-white">{member.name}</div>
                          <div className="text-xs text-gray-400 hidden sm:block">{member.phone}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 hidden sm:table-cell">
                      {member.gender === 'male' ? '남' : member.gender === 'female' ? '여' : '-'}
                      {getAge(member.birthDate) && ` · ${getAge(member.birthDate)}세`}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 hidden md:table-cell">
                      {member.phone || '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 hidden lg:table-cell">
                      {member.purpose || '-'}
                    </td>
                    <td className="px-4 py-3">
                      <span className={getStatusBadgeClass(member.status)}>
                        {getStatusLabel(member.status)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 hidden lg:table-cell">
                      {formatDate(member.createdAt)}
                    </td>
                    <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => handleEdit(member)}
                          className="p-1.5 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                          title="수정"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(member)}
                          className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                          title="삭제"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* 하단 정보 + 페이지네이션 */}
        <div className="px-4 py-3 border-t border-gray-100 dark:border-gray-700 flex flex-col sm:flex-row items-center justify-between gap-3">
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {filtered.length === 0 ? '검색 결과 없음' : `전체 ${filtered.length}명 중 ${(currentPage - 1) * ITEMS_PER_PAGE + 1}–${Math.min(currentPage * ITEMS_PER_PAGE, filtered.length)}명`}
          </span>
          <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
        </div>
      </div>

      {/* 회원 등록/수정 모달 */}
      <Modal
        isOpen={isFormOpen}
        onClose={handleCloseForm}
        title={editingMember ? '회원 수정' : '회원 등록'}
        size="lg"
      >
        <MemberForm member={editingMember} onClose={handleCloseForm} />
      </Modal>

      {/* 삭제 확인 모달 */}
      <Modal
        isOpen={!!deleteConfirm}
        onClose={cancelDelete}
        title="회원 삭제"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            <span className="font-semibold text-gray-900 dark:text-white">{deleteConfirm?.name}</span> 회원을 삭제합니다.<br />
            삭제된 데이터는 복구할 수 없습니다.
          </p>
          <div>
            <label className="label">
              확인을 위해 <span className="font-bold text-red-500">삭제</span>를 입력하세요
            </label>
            <input
              type="text"
              value={deleteInput}
              onChange={e => setDeleteInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && confirmDelete()}
              className="input"
              placeholder="삭제"
              autoFocus
            />
          </div>
          <div className="flex justify-end gap-3 pt-1">
            <button onClick={cancelDelete} className="btn-secondary">취소</button>
            <button
              onClick={confirmDelete}
              disabled={deleteInput !== '삭제'}
              className="px-4 py-2 rounded-lg text-sm font-medium bg-red-600 text-white hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              삭제
            </button>
          </div>
        </div>
      </Modal>

      {/* 엑셀 가져오기 모달 */}
      <ImportMembersModal isOpen={isImportOpen} onClose={() => setIsImportOpen(false)} />
    </div>
  );
};

export default Members;
